/**
 * cameraImageUtil
 *
 * Normalizes a camera-captured image so its encoded file size lands within the
 * [min, max] range required by GCPCloudImageUploader.validateFile (128 KB – 3 MB).
 *
 * - Too large  -> lower JPEG quality, then downscale dimensions, re-encoding until <= max.
 * - Too small  -> raise quality toward 1.0, then upscale dimensions; as a guaranteed
 *                 last resort, append a JPEG COM (0xFFFE) comment segment padded with
 *                 bytes so the file reaches >= min without corrupting the image.
 *
 * Pure / DOM-only. Guarded for SSR.
 *
 * The whole behavior is gated by CAMERA_IMAGE_SIZE.enabled so it can be reverted by
 * flipping a single flag.
 */

// Single place to toggle / tune this change. Set `enabled: false` to revert to the
// previous behavior (raw capture at quality 0.92, subject to the existing validation).
export const CAMERA_IMAGE_SIZE = {
  enabled: true,
  min: 131072, // 128 KB  (must match GCPCloudImageUploader.validateFile)
  max: 3145728, // 3 MB
  maxEdgePx: 3000, // upscale safety cap (longest edge)
};

const MIME = 'image/jpeg';

const encode = (canvas, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), MIME, quality);
  });

/** Draw `source` onto a new canvas scaled to width x height. */
const scaleCanvas = (source, width, height) => {
  const target = document.createElement('canvas');
  target.width = Math.max(1, Math.round(width));
  target.height = Math.max(1, Math.round(height));
  const ctx = target.getContext('2d');
  ctx.drawImage(source, 0, 0, target.width, target.height);
  return target;
};

/**
 * Append a JPEG COM (comment) marker segment after the SOI so the file size reaches
 * `targetSize`. A COM segment is `FF FE <2-byte length> <payload>`; its max payload is
 * 65533 bytes, so multiple segments are inserted if needed. Decoders ignore COM data.
 */
const padJpegToSize = async (blob, targetSize) => {
  const buffer = new Uint8Array(await blob.arrayBuffer());
  // Valid JPEG starts with SOI (FF D8). If not, return as-is (nothing safe to do).
  if (buffer.length < 2 || buffer[0] !== 0xff || buffer[1] !== 0xd8) {
    return blob;
  }

  let bytesNeeded = targetSize - buffer.length;
  if (bytesNeeded <= 0) return blob;

  const MAX_PAYLOAD = 65533; // 65535 - 2 length bytes
  const segments = [];
  while (bytesNeeded > 0) {
    // each segment costs 4 bytes of overhead (FF FE + 2 length bytes)
    const payload = Math.min(MAX_PAYLOAD, Math.max(1, bytesNeeded - 4));
    const segLen = payload + 2; // length field counts itself (2) + payload
    const seg = new Uint8Array(2 + 2 + payload);
    seg[0] = 0xff;
    seg[1] = 0xfe; // COM marker
    seg[2] = (segLen >> 8) & 0xff;
    seg[3] = segLen & 0xff;
    // payload bytes default to 0x00 (already zero-filled)
    segments.push(seg);
    bytesNeeded -= seg.length;
  }

  const totalSegBytes = segments.reduce((sum, s) => sum + s.length, 0);
  const out = new Uint8Array(buffer.length + totalSegBytes);
  // SOI
  out[0] = 0xff;
  out[1] = 0xd8;
  let offset = 2;
  // inserted COM segments right after SOI
  for (const seg of segments) {
    out.set(seg, offset);
    offset += seg.length;
  }
  // rest of the original JPEG (everything after SOI)
  out.set(buffer.subarray(2), offset);

  return new Blob([out], { type: MIME });
};

/**
 * @param {HTMLCanvasElement} canvas  canvas holding the captured frame
 * @param {{min:number,max:number,maxEdgePx:number}} opts
 * @returns {Promise<Blob>} JPEG blob with min <= size <= max (best effort, guaranteed >= min)
 */
export const normalizeImageToSizeRange = async (canvas, opts = {}) => {
  const min = opts.min ?? CAMERA_IMAGE_SIZE.min;
  const max = opts.max ?? CAMERA_IMAGE_SIZE.max;
  const maxEdgePx = opts.maxEdgePx ?? CAMERA_IMAGE_SIZE.maxEdgePx;

  if (typeof document === 'undefined') {
    return encode(canvas, 0.92);
  }

  // Start from a high-quality encode at native resolution.
  let working = canvas;
  let blob = await encode(working, 0.92);

  // ---- Case 1: too large -> reduce ----
  if (blob && blob.size > max) {
    // Step A: drop quality.
    const qualities = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35];
    for (let i = 0; i < qualities.length && blob.size > max; i += 1) {
      blob = await encode(working, qualities[i]); // eslint-disable-line no-await-in-loop
    }
    // Step B: if still too large, downscale dimensions and re-encode at mid quality.
    let guard = 0;
    while (blob && blob.size > max && guard < 12) {
      working = scaleCanvas(working, working.width * 0.85, working.height * 0.85);
      blob = await encode(working, 0.7); // eslint-disable-line no-await-in-loop
      guard += 1;
    }
    return blob;
  }

  // ---- Case 2: too small -> grow ----
  if (blob && blob.size < min) {
    // Step A: maximize quality.
    const hiBlob = await encode(working, 1.0);
    if (hiBlob && hiBlob.size >= blob.size) blob = hiBlob;

    // Step B: upscale dimensions (more pixels => bigger file), capped by maxEdgePx and max.
    let guard = 0;
    while (blob && blob.size < min && guard < 10) {
      const longestEdge = Math.max(working.width, working.height);
      if (longestEdge * 1.25 > maxEdgePx) break;
      const upscaled = scaleCanvas(working, working.width * 1.25, working.height * 1.25);
      const upBlob = await encode(upscaled, 1.0); // eslint-disable-line no-await-in-loop
      if (!upBlob || upBlob.size > max) break; // don't overshoot the max
      working = upscaled;
      blob = upBlob;
      guard += 1;
    }

    // Step C: guaranteed floor via COM-segment padding (kept under max).
    if (blob && blob.size < min) {
      const target = Math.min(min, max);
      blob = await padJpegToSize(blob, target);
    }
    return blob;
  }

  // Already within range (or encode failed -> return whatever we have).
  return blob;
};
